// Data layer shared by the two dashboards (Pages/dasboard/Dashboard.jsx for one
// branch, GroupDashboard.jsx for all of them).
//
// Everything here reads the endpoints the app already has - the sales report
// (one row per invoice), product-wise sale and the pay in / out report. What
// those cannot answer (profit, targets, receivables, stock, purchases) is not
// faked: the screens show those blocks as "needs backend", and the list lives
// in docs/BACKEND-COMPLETE.md (Part 8).
import dayjs from "dayjs";
import { getPayInOutReport, getSalesReport } from "../../../services/ReportApiService";
import { getProductWiseSale } from "../../../services/DashBoardApiService";
import {
  DatatablePagingParameter,
  Filterclass,
} from "../../../core/json/DatatablePagingParameters";

/* ================= formatting ================= */
export const num = (v) => Number(v) || 0;
export const iso = (d) => dayjs(d).format("YYYY-MM-DD");
export const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const money = (n) =>
  (n < 0 ? "-" : "") + Math.abs(Math.round(num(n))).toLocaleString("en-US");
export const moneyDp = (n, dp = 2) =>
  num(n).toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp });

// 1 234 567 -> "1.23M", 245 000 -> "245K", below that the whole figure
export const compact = (n) => {
  const a = Math.abs(num(n));
  if (a >= 1000000) return (n / 1000000).toFixed(a >= 10000000 ? 1 : 2).replace(/\.0+$/, "") + "M";
  if (a >= 100000) return Math.round(n / 1000).toLocaleString("en-US") + "K";
  return money(n);
};
export const compactK = (n) => {
  const a = Math.abs(num(n));
  if (a >= 1000000) return compact(n);
  if (a >= 1000) return Math.round(n / 1000).toLocaleString("en-US") + "K";
  return money(n);
};

export const pct = (n, d) => (d ? (n / d) * 100 : 0);
export const fmtPct = (n, dp = 1) => (n >= 0 ? "" : "-") + Math.abs(num(n)).toFixed(dp) + "%";
export const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");
export const dayLabel = (s) => {
  const d = dayjs(s);
  return d.isValid() ? `${d.date()} ${MON[d.month()]}` : "—";
};
export const longDate = (s) => dayjs(s).format("dddd, D MMMM YYYY");
export const hourLbl = (h) => String(h).padStart(2, "0") + ":00";
export const initials = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "—";

/* ================= period model ================= */
export const PERIODS = [
  { id: "today", label: "Today", key: "t" },
  { id: "yest", label: "Yesterday", key: "y" },
  { id: "7d", label: "Last 7 days", key: "w" },
  { id: "30d", label: "Last 30 days", key: "m" },
  { id: "mtd", label: "This month" },
  { id: "qtd", label: "This quarter", key: "q" },
];

export const CMP_OPTIONS = [
  { v: "prev", t: "Previous period" },
  { v: "lw", t: "Same period last week" },
  { v: "ly", t: "Same period last year" },
];

export function rangeFor(id) {
  const t = dayjs().startOf("day");
  const mk = (a, b, name) => ({
    id,
    from: iso(a),
    to: iso(b),
    n: b.diff(a, "day") + 1,
    name,
    live: iso(b) === iso(t),
  });
  switch (id) {
    case "yest":
      return mk(t.subtract(1, "day"), t.subtract(1, "day"), "yesterday");
    case "7d":
      return mk(t.subtract(6, "day"), t, "the last 7 days");
    case "30d":
      return mk(t.subtract(29, "day"), t, "the last 30 days");
    case "mtd":
      return mk(t.startOf("month"), t, "this month");
    case "qtd":
      return mk(t.month(Math.floor(t.month() / 3) * 3).startOf("month"), t, "this quarter");
    default:
      return mk(t, t, "today");
  }
}

export function cmpRange(r, cmp) {
  const shift = cmp === "lw" ? 7 : cmp === "ly" ? 364 : r.n;
  let name;
  if (cmp === "lw") name = r.n === 1 ? "same day last week" : "the same period last week";
  else if (cmp === "ly") name = r.n === 1 ? "same day last year" : "the same period last year";
  else name = r.n === 1 ? (r.id === "today" ? "yesterday" : "the day before") : `the previous ${r.n} days`;
  return {
    from: iso(dayjs(r.from).subtract(shift, "day")),
    to: iso(dayjs(r.to).subtract(shift, "day")),
    n: r.n,
    name,
  };
}

// the 12 days ending on the range's last day - the sparklines
export const sparkRange = (r) => ({ from: iso(dayjs(r.to).subtract(11, "day")), to: r.to });

export const daysOf = (from, to) => {
  const out = [];
  for (let d = dayjs(from); !d.isAfter(dayjs(to)); d = d.add(1, "day")) out.push(iso(d));
  return out;
};

// fold overlapping / touching spans so each day is fetched once
export function mergeSpans(spans) {
  const s = spans.filter((x) => x && x.from && x.to).map((x) => ({ ...x })).sort((a, b) => (a.from < b.from ? -1 : 1));
  const out = [];
  s.forEach((x) => {
    const last = out[out.length - 1];
    if (last && x.from <= iso(dayjs(last.to).add(1, "day"))) {
      if (x.to > last.to) last.to = x.to;
    } else out.push({ from: x.from, to: x.to });
  });
  return out;
}

/* ================= invoices (sales report) ================= */
const PAYNAME = { 0: "Cash", 1: "Card", 4: "Credit", 5: "Complimentary", 6: "Multi-payment", 8: "UPI" };
export const payName = (t) => PAYNAME[t] || "Other";

// minute of the day an invoice was raised, or null when the API sends a bare date
const minsOf = (...vals) => {
  for (const v of vals) {
    if (!v) continue;
    const d = dayjs(v);
    if (!d.isValid()) continue;
    const m = d.hour() * 60 + d.minute();
    if (m > 0) return m;
  }
  return null;
};

export const normInvoice = (r, i) => ({
  id: r?.guid || r?.id || `${r?.billNo}-${i}`,
  billNo: r?.billNo || "",
  date: r?.transactionDate ? iso(r.transactionDate) : "",
  mins: minsOf(r?.transactionDate, r?.billDate),
  net: num(r?.netTotal),
  gross: num(r?.rateTotal),
  vat: num(r?.vatAmount),
  discount: num(r?.discount),
  cash: num(r?.cash),
  card: num(r?.card),
  payType: Number(r?.paymentType),
  status: Number(r?.salesStatus),
  cashier: r?.user?.name || "",
  counter: r?.counter?.name || "",
  customer: r?.customer?.name || r?.customerName || "",
});

export async function fetchInvoices(branch, from, to) {
  const DatatablePagingParameters = new DatatablePagingParameter();
  DatatablePagingParameters.PageNumber = 1;
  DatatablePagingParameters.PageSize = -1;
  const Filters = new Filterclass();
  Filters.Branch = branch;
  Filters.FromDate = from;
  Filters.ToDate = to;
  const res = await getSalesReport({ DatatablePagingParameters, Filters });
  const list = res?.data?.salesReportDetail;
  return Array.isArray(list) ? list.map(normInvoice) : [];
}

// one call per merged span - current, comparison and sparkline windows together
export async function fetchInvoiceSpans(branch, spans) {
  const parts = await Promise.all(mergeSpans(spans).map((s) => fetchInvoices(branch, s.from, s.to)));
  return parts.flat();
}

export const inRange = (rows, a, b) => rows.filter((r) => r.date >= a && r.date <= b);
export const hasTimes = (rows) => rows.some((r) => r.mins !== null);

// A live period is compared "to this hour": on the comparison's last day, drop
// the invoices raised later in the day than now. Needs invoice times.
export function paceCut(rows, lastDay, live) {
  if (!live || !hasTimes(rows)) return rows;
  const now = dayjs();
  const cut = now.hour() * 60 + now.minute();
  return rows.filter((r) => r.date !== lastDay || r.mins === null || r.mins <= cut);
}

// the figure a row contributes: net total, or net minus VAT when VAT is off
export const valueOf = (vatOn) => (r) => (vatOn ? r.net : r.net - r.vat);
export const sumOf = (rows, f) => rows.reduce((a, r) => a + f(r), 0);

export function dailyTotals(rows, from, to, f) {
  const m = {};
  rows.forEach((r) => (m[r.date] = (m[r.date] || 0) + f(r)));
  return daysOf(from, to).map((d) => ({ date: d, v: m[d] || 0 }));
}

// sales per trading hour; the window runs from the first to the last hour
// with a sale in either set (to now, when live)
export function hourWindow(sets, live) {
  let lo = 24;
  let hi = -1;
  sets.forEach((rows) =>
    rows.forEach((r) => {
      if (r.mins === null) return;
      const h = Math.floor(r.mins / 60);
      if (h < lo) lo = h;
      if (h > hi) hi = h;
    })
  );
  if (hi < 0) return null;
  if (live) hi = Math.max(hi, dayjs().hour());
  return { lo, hi };
}
export function hourlyTotals(rows, win, f) {
  const out = [];
  for (let h = win.lo; h <= win.hi; h++) out.push({ h, v: 0, n: 0 });
  rows.forEach((r) => {
    if (r.mins === null) return;
    const o = out[Math.floor(r.mins / 60) - win.lo];
    if (o) {
      o.v += f(r);
      o.n += 1;
    }
  });
  return out;
}

// cash / card / credit / other, scaled so the parts add up to the shown total
export function paymentMix(rows, vatOn) {
  const net = sumOf(rows, (r) => r.net);
  const shown = sumOf(rows, valueOf(vatOn));
  const k = net ? shown / net : 0;
  const cash = sumOf(rows, (r) => r.cash);
  const card = sumOf(rows, (r) => r.card);
  const credit = sumOf(rows, (r) => (r.payType === 4 ? r.net : 0));
  const other = Math.max(0, net - cash - card - credit);
  return [
    { n: "Cash", v: cash * k, c: "var(--s1)" },
    { n: "Card", v: card * k, c: "var(--s2)" },
    { n: "Credit (on account)", v: credit * k, c: "var(--s3)" },
    { n: "Other / complimentary", v: other * k, c: "var(--s4)" },
  ];
}

// group invoices by a text key -> [{name, v, n}], biggest first
export function rankBy(rows, keyFn, f) {
  const m = {};
  rows.forEach((r) => {
    const k = keyFn(r);
    if (!k) return;
    if (!m[k]) m[k] = { name: k, v: 0, n: 0 };
    m[k].v += f(r);
    m[k].n += 1;
  });
  return Object.values(m).sort((a, b) => b.v - a.v);
}

/* ================= product-wise sale ================= */
export async function fetchItemSales(branch, from, to) {
  const data = await getProductWiseSale(branch, from, to);
  const m = {};
  (Array.isArray(data) ? data : []).forEach((r) => {
    const name = r?.product?.name;
    if (!name) return;
    if (!m[name]) m[name] = { name, value: 0, qty: 0, days: {} };
    const o = m[name];
    o.value += num(r.billTotal);
    o.qty += num(r.quantity);
    const d = r.transactionDate ? iso(r.transactionDate) : "";
    if (!o.days[d]) o.days[d] = { value: 0, qty: 0 };
    o.days[d].value += num(r.billTotal);
    o.days[d].qty += num(r.quantity);
  });
  return Object.values(m);
}

/* ================= pay in / out ================= */
// isPayIn === 1 is a pay OUT - the same reading as the Pay In/Out report
export async function fetchPayInOut(branch, from, to) {
  const DatatablePagingParameters = new DatatablePagingParameter();
  DatatablePagingParameters.PageNumber = 1;
  DatatablePagingParameters.PageSize = -1;
  const Filters = new Filterclass();
  Filters.Branch = branch;
  Filters.FromDate = from;
  Filters.ToDate = to;
  const res = await getPayInOutReport({ DatatablePagingParameters, Filters });
  const list = res?.data?.payInOutReportDetail;
  return (Array.isArray(list) ? list : []).map((r, i) => ({
    id: r?.guid || r?.id || `pio-${i}`,
    date: r?.date ? iso(r.date) : "",
    mins: minsOf(r?.date),
    amount: num(r?.amount),
    out: r?.isPayIn === 1,
    account: r?.accountType?.name || "",
    narration: r?.narration || "",
  }));
}

/* ================= misc ================= */
export const deltaPct = (cur, prev) => (prev ? ((cur - prev) / Math.abs(prev)) * 100 : null);

export function downloadCsv(name, header, lines) {
  const cell = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [header, ...lines].map((l) => l.map(cell).join(",")).join("\r\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" }));
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(a.href), 1500);
}
